import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Loader2,
  Zap,
  Save,
  Settings2,
  BarChart3,
  Lock,
} from "lucide-react";

const FACTOR_LABELS = {
  source: "Data Source",
  turnover: "GST Turnover",
  business_type: "Business Type",
  brand: "Brand / Ecommerce",
  gst_age: "GST Registration Age",
  call_history: "Call History",
  credit: "Credit Line",
};

const FACTOR_DESCRIPTIONS = {
  source: "Order Exists > Referral > Query Exists > Website > Meta",
  turnover: "5-25Cr sweet spot, then 1.5-5Cr, then 25-100Cr",
  business_type: "Partnership > LLP > Proprietorship > Pvt Ltd > Public",
  brand: "D2C / Marketplace brand detected via website crawl",
  gst_age: "3+ years = 100, less than 3 years = 40",
  call_history: "Talk time + connection rate from past calls",
  credit: "Credit line opened = committed buyer (only for existing customers)",
};

const SmartAllocation = () => {
  const [config, setConfig] = useState(null);
  const [editWeights, setEditWeights] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const isSuperAdmin = currentUser?.email === "admin@locofast.com";

  useEffect(() => {
    fetchConfig();
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setCurrentUser(res.data);
    } catch {}
  };

  const fetchConfig = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/scoring-config`);
      setConfig(res.data);
      setEditWeights(res.data.weights || {});
    } catch (err) {
      toast.error("Failed to load scoring config");
    } finally {
      setIsLoading(false);
    }
  };

  const totalWeight = Object.values(editWeights).reduce((sum, v) => sum + (parseInt(v) || 0), 0);

  const handleSave = async () => {
    if (totalWeight !== 100) {
      toast.error(`Weights must sum to 100. Current total: ${totalWeight}`);
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        ...config,
        weights: Object.fromEntries(Object.entries(editWeights).map(([k, v]) => [k, parseInt(v) || 0])),
      };
      await axios.put(`${API}/scoring-config`, payload);
      toast.success("Scoring weights updated");
      setConfig(payload);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
          Lead Scoring Settings
        </h1>
        <p className="text-slate-500 mt-1">
          Configure ICP scoring weights to prioritize leads by conversion probability
        </p>
      </div>

      {/* Weights Editor */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-blue-600" />
              Scoring Weights
            </CardTitle>
            <div className={`text-sm font-bold px-3 py-1 rounded-full ${totalWeight === 100 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
              Total: {totalWeight}%
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(FACTOR_LABELS).map(([key, label]) => (
            <div key={key} className="flex items-center gap-4 p-3 rounded-lg hover:bg-slate-50 border border-slate-100">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800">{label}</p>
                <p className="text-xs text-slate-400 mt-0.5">{FACTOR_DESCRIPTIONS[key]}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {isSuperAdmin ? (
                  <>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={editWeights[key] || 0}
                      onChange={(e) => setEditWeights(prev => ({ ...prev, [key]: e.target.value }))}
                      className="w-20 h-9 text-center font-semibold"
                      data-testid={`weight-${key}`}
                    />
                    <span className="text-sm text-slate-400">%</span>
                  </>
                ) : (
                  <span className="text-lg font-bold text-slate-700 w-16 text-center">
                    {editWeights[key] || 0}%
                  </span>
                )}
              </div>
              {/* Visual bar */}
              <div className="w-24 bg-slate-100 rounded-full h-2 hidden sm:block">
                <div
                  className="h-2 rounded-full bg-blue-500 transition-all"
                  style={{ width: `${(editWeights[key] || 0)}%` }}
                />
              </div>
            </div>
          ))}

          {/* Save button — super admin only */}
          {isSuperAdmin ? (
            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
              <p className="text-xs text-slate-400">
                {totalWeight === 100 ? "Weights are balanced" : `Adjust to reach 100% (currently ${totalWeight}%)`}
              </p>
              <Button
                onClick={handleSave}
                disabled={isSaving || totalWeight !== 100}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="save-weights-btn"
              >
                {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Weights
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 pt-4 border-t border-slate-100 text-sm text-slate-500">
              <Lock className="w-4 h-4" />
              Only super admin can edit scoring weights
            </div>
          )}
        </CardContent>
      </Card>

      {/* Factor Details */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-slate-500" />
            How Each Factor Scores (0-100 per factor)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Data Source */}
            <div className="border border-slate-100 rounded-lg p-3 space-y-2">
              <p className="text-sm font-semibold text-slate-700">Data Source</p>
              <div className="space-y-1 text-xs text-slate-600">
                <div className="flex justify-between"><span>Order Exists</span><span className="font-bold">100</span></div>
                <div className="flex justify-between"><span>Referral</span><span className="font-bold">85</span></div>
                <div className="flex justify-between"><span>Query Exists</span><span className="font-bold">70</span></div>
                <div className="flex justify-between"><span>Website</span><span className="font-bold">55</span></div>
                <div className="flex justify-between"><span>E-Commerce Data</span><span className="font-bold">50</span></div>
                <div className="flex justify-between"><span>General / New</span><span className="font-bold">20-30</span></div>
              </div>
            </div>

            {/* Turnover */}
            <div className="border border-slate-100 rounded-lg p-3 space-y-2">
              <p className="text-sm font-semibold text-slate-700">GST Turnover</p>
              <div className="space-y-1 text-xs text-slate-600">
                <div className="flex justify-between"><span>5Cr - 25Cr (sweet spot)</span><span className="font-bold text-emerald-600">100</span></div>
                <div className="flex justify-between"><span>1.5Cr - 5Cr</span><span className="font-bold">75</span></div>
                <div className="flex justify-between"><span>25Cr - 100Cr</span><span className="font-bold">60</span></div>
                <div className="flex justify-between"><span>40L - 1.5Cr</span><span className="font-bold">40</span></div>
                <div className="flex justify-between"><span>100Cr - 500Cr</span><span className="font-bold">35</span></div>
                <div className="flex justify-between"><span>500Cr+ / 0-40L</span><span className="font-bold">20-25</span></div>
              </div>
            </div>

            {/* Business Type */}
            <div className="border border-slate-100 rounded-lg p-3 space-y-2">
              <p className="text-sm font-semibold text-slate-700">Business Type</p>
              <div className="space-y-1 text-xs text-slate-600">
                <div className="flex justify-between"><span>Partnership Firm</span><span className="font-bold text-emerald-600">100</span></div>
                <div className="flex justify-between"><span>LLP</span><span className="font-bold">95</span></div>
                <div className="flex justify-between"><span>Proprietorship</span><span className="font-bold">90</span></div>
                <div className="flex justify-between"><span>Proprietorship (Rajasthan/08)</span><span className="font-bold text-emerald-600">100</span></div>
                <div className="flex justify-between"><span>Private Limited</span><span className="font-bold">70</span></div>
                <div className="flex justify-between"><span>Public Limited</span><span className="font-bold">40</span></div>
              </div>
            </div>

            {/* GST Age */}
            <div className="border border-slate-100 rounded-lg p-3 space-y-2">
              <p className="text-sm font-semibold text-slate-700">GST Registration Age</p>
              <div className="space-y-1 text-xs text-slate-600">
                <div className="flex justify-between"><span>3+ years</span><span className="font-bold text-emerald-600">100</span></div>
                <div className="flex justify-between"><span>&lt;3 years</span><span className="font-bold">40</span></div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SmartAllocation;
